import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099QInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-Q instructions for Tax Year 2025"
          description="Get clear instructions for Form 1099-Q for 2025. TaxZerone helps you report distributions from 529 and other qualified education plans correctly."
          keywords=""
          canonical="resources/information-returns/form-1099-q-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-Q</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-q-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-Q at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-Q,{' '}
                        </strong>{' '}
                        reports distributions from{' '}
                        <strong style={{ fontWeight: 600 }}>
                          529 plans (Qualified Tuition Programs) and Coverdell
                          Education Savings Accounts (ESAs)
                        </strong>{' '}
                        to the IRS and the recipient.
                      </span>
                    </li>

                    <li>
                      <span>
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        (paper filing) or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31{' '}
                        </strong>{' '}
                        (electronic filing).
                      </span>
                    </li>

                    <li>
                      <span>
                        This article covers line-by-line instructions including
                        payer/trustee and recipient information,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>{' '}
                        (Gross Distribution),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 2</strong>{' '}
                        (Earnings),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 3</strong>{' '}
                        (Basis),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 4a</strong>{' '}
                        (Trustee-to-Trustee Transfer),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 4b</strong>{' '}
                        (QTP to Roth IRA Transfer),{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Boxes 5a-5c
                        </strong>{' '}
                        (Type of Qualified Education Program), and{' '}
                        <strong style={{ fontWeight: 600 }}>Box 6</strong>{' '}
                        (Designated Beneficiary Checkbox).
                      </span>
                    </li>

                    <li>
                      <span>
                        Late or incorrect filing may result in IRS penalties
                        ranging from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-Q?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  IRS Form 1099-Q is used to report distributions from qualified
                  education programs, such as 529 plans and Coverdell ESAs, to
                  both the IRS and the recipient. These distributions cover
                  educational expenses for designated beneficiaries.
                </Typography>
                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 1099-Q?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Any financial institution, educational institution, or entity
                  that administers a qualified education program and makes
                  distributions of $10 or more during the tax year must file
                  Form 1099-Q. This form ensures that both the IRS and the
                  recipient are informed about the taxable distributions.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-Q due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  There are two key deadlines related to Form 1099-Q that
                  trustees/payers must be aware of:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for trustees/payers to file Form 1099-Q with the
                  IRS depends on the filing method:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>E-filing deadline: March 31st, 2026</strong>
                    <br />
                    If you&apos;re e-filing the Form 1099-Q to the IRS, the
                    deadline is March 31st of the year following the tax year
                    for which the form is being issued
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the last-minute 1099-Q deadline stress
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now for easy IRS compliance and
                          peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099qinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28th, 2026*</strong>
                    <br /> If you&apos;re filing Form 1099-Q with the IRS using
                    traditional paper filing methods, the deadline is earlier —
                    February 28th of the year following the tax year for which
                    the form is being issued.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31st, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Trustees/payers are required to provide copies of Form 1099-Q
                  to the recipients (individuals or entities that received
                  interest payments) by January 31st of the year following the
                  tax year for which the form is being issued. This ensures that
                  recipients have the necessary information to accurately report
                  their interest income when filing their tax returns.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> *if the due date falls on the weekend or federal
                  holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  It is crucial for payers to meet these deadlines to avoid
                  potential penalties from the IRS for failure to file Form
                  1099-Q accurately and on time. Penalties may be imposed for
                  late filing, failure to provide recipient copies, or reporting
                  incorrect information on the form.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-Q penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-q/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1099-Q
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty for each
                  missed return. Below is the breakdown of Form 1099-Q
                  penalties:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      Choose TaxZerone to e-file your 1099-Q forms ahead of the
                      deadline.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099qinstructions`}
                    >
                      E-file Form 1099-Q
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-Q Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-Q.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Trustee/Payer details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-q-payer.webp" />
                  <source srcSet="/assets/images/form-1099-q-payer.png" />
                  <img
                    src="/assets/images/form-1099-q-payer.png"
                    loading="lazy"
                    alt="Form 1099-q-trustee/payer’s details"
                    width={590}
                    height={233}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the payer/trustee’s name, complete address, and TIN (SSN
                  if you&apos;re an individual; EIN if you&apos;re a business).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-q-recipent.webp" />
                  <source srcSet="/assets/images/form-1099-q-recipent.png" />
                  <img
                    src="/assets/images/form-1099-q-recipent.png"
                    loading="lazy"
                    alt="Recipient details"
                    width={590}
                    height={319}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  Enter the recipient’s name, complete address, and TIN (SSN for
                  an individual; EIN for a business).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Gross Distribution
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-q-gross-distribution.webp" />
                  <source srcSet="/assets/images/form-1099-q-gross-distribution.png" />
                  <img
                    src="/assets/images/form-1099-q-gross-distribution.png"
                    loading="lazy"
                    alt="Box 1: Gross Distribution"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For Qualified Tuition Programs (QTPs):
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Includes cash or in-kind distributions for:
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      Tuition credits or certificates
                    </li>
                    <li style={{ marginBottom: 12 }}>Payment vouchers</li>
                    <li style={{ marginBottom: 12 }}>Tuition waivers</li>
                    <li style={{ marginBottom: 12 }}>Similar items</li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    Also includes refunds to account owner, designated
                    beneficiary, and beneficiary (in case of death or
                    disability)
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For Coverdell Education Savings Accounts (ESAs):
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Includes refunds, payments upon death or disability, and
                    withdrawals of excess contributions plus earnings
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Earnings
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-q-earnings.webp" />
                  <source srcSet="/assets/images/form-1099-q-earnings.png" />
                  <img
                    src="/assets/images/form-1099-q-earnings.png"
                    loading="lazy"
                    alt="Box 2: Earnings"
                    width={245}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter earnings in Box 2. If there&apos;s a loss:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter zero if it&apos;s not the final distribution year or
                    if there are no earnings.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Only report the loss if it&apos;s the final distribution
                    year.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Earnings are not subject to backup withholding.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you are reporting a distribution from a Coverdell ESA that
                  includes a returned contribution plus earnings, file two
                  separate Forms:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    One for the returned contribution plus earnings
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    One for the distribution of the other part of the account
                  </li>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Basis
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-q-basis.webp" />
                  <source srcSet="/assets/images/form-1099-q-basis.png" />
                  <img
                    src="/assets/images/form-1099-q-basis.png"
                    loading="lazy"
                    alt="Box 3: Basis"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For Qualified Tuition Programs (QTPs) and Coverdell Education
                  Savings Accounts (ESAs):
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the basis amount in Box 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Calculation:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Box 3 amount = Box 1 (Gross Distribution) - Box 2 (Earnings)
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Only enter an amount in Box 3 if you can
                  determine the basis.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Type of Transfer
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-q-trustee.webp" />
                  <source srcSet="/assets/images/form-1099-q-trustee.png" />
                  <img
                    src="/assets/images/form-1099-q-trustee.png"
                    loading="lazy"
                    alt="Box 4: Type of Transfer"
                    width={296}
                    height={107}
                    className="proportional-img"
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    mt: 2,
                  }}
                >
                  Box 4a: Trustee-to-Trustee Transfer
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box for direct transfers:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  For Qualified Tuition Programs (QTPs):
                </Typography>
                <ul>
                  <li style={{ marginBottom: 12 }}>
                    From one QTP to another QTP
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    From a QTP to an ABLE account
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  For Coverdell Education Savings Accounts (ESAs):
                </Typography>
                <ul>
                  <li style={{ marginBottom: 12 }}>
                    From one Coverdell ESA to another Coverdell ESA
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    From a Coverdell ESA to a QTP
                  </li>
                </ul>{' '}
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Important for transfers between qualified education programs:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    The distributing program must provide you a statement
                    showing the{' '}
                    <span style={{ fontWeight: 600 }}> earnings portion</span>{' '}
                    of the distribution
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    This statement is due{' '}
                    <span style={{ fontWeight: 600 }}>
                      within 30 days of the distribution or by January 10,
                    </span>{' '}
                    whichever is earlier
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    Use this information to properly calculate the earnings (or
                    loss) reported in{' '}
                    <span style={{ fontWeight: 600 }}>Box 2</span> and the basis
                    reported in <span style={{ fontWeight: 600 }}>Box 3</span>
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    mt: 2,
                  }}
                >
                  Box 4b: QTP to Roth IRA Transfer
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if the distribution was made directly (
                  <span style={{ fontWeight: 600 }}>
                    trustee-to-trustee transfer
                  </span>
                  ){' '}
                  <span style={{ fontWeight: 600 }}>
                    from a QTP to a Roth IRA
                  </span>{' '}
                  maintained for the benefit of the QTP beneficiary.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 5a-5c: Distribution Is From
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-q-distribution-form.webp" />
                  <source srcSet="/assets/images/form-1099-q-distribution-form.png" />
                  <img
                    src="/assets/images/form-1099-q-distribution-form.png"
                    loading="lazy"
                    alt="Box 5a-5c: Distribution Is From"
                    width={297}
                    height={143}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box 5a: Private QTP
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the distribution is from a{' '}
                  <span style={{ fontWeight: 600 }}>
                    Qualified Tuition Program (QTP)
                  </span>{' '}
                  established by one or more{' '}
                  <span style={{ fontWeight: 600 }}>
                    private eligible educational institutions.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box 5b: State QTP
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the distribution is from a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    QTP established by a state
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box 5c: Coverdell ESA
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if the distribution is from a{' '}
                  <span style={{ fontWeight: 600 }}>
                    Coverdell Education Savings Account (ESA).
                  </span>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Designated Beneficiary Checkbox
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-box-checked.webp" />
                  <source srcSet="/assets/images/form-1099-box-checked.png" />
                  <img
                    src="/assets/images/form-1099-box-checked.png"
                    loading="lazy"
                    alt="Box 6: Designated Beneficiary Checkbox"
                    width={245}
                    height={117}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Check the box if the recipient is not the designated
                  beneficiary under a QTP or a Coverdell ESA.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 1099-Q?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 1099-Q can be filed electronically or by postal mail.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form 1099-Q electronic filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-q/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-Q
                  </Link>{' '}
                  as the process is efficient and straightforward. E-filing
                  offers the advantage of receiving notifications promptly after
                  the IRS processes your submission.
                </Typography>
                {/* <Typography variant="body1" sx={{ mb: 2 }}>
                  To e-file Form 1099-Q, you can choose an IRS-authorized e-file
                  service provider, such as{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    TaxZerone
                  </Link>
                  .
                </Typography> */}
                <Typography variant="body1" sx={{ mb: 3 }}>
                  With TaxZerone, the e-filing process is streamlined and can be
                  completed within minutes. You simply need to fill out the
                  form, review the information, transmit it to the IRS
                  electronically, and provide the recipient&apos;s copy.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you prefer to file Form 1099-Q via traditional paper
                  methods, here are the steps to follow:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download and print Form 1099-Q from the IRS website (
                    <Link
                      href="https://www.irs.gov/"
                      target="_blank"
                      sx={{ color: '#2020ff' }}
                    >
                      www.irs.gov
                    </Link>
                    ).
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Complete the required fields, including the recipient&apos;s
                    name, address, TIN, and distribution amount. Also, provide
                    your business name, address, and TIN.
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Mail the completed paper form to the IRS at the address
                    specified in the 1099 instructions. Refer to the &quot;Where
                    to Send Form 1099-Q - Mailing Address&quot; section to find
                    the correct address for your state.
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Send a copy of the 1099-Q to the recipient by January 31st
                    for their tax reporting purposes.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing:
                  </Typography>
                  <ul style={{ marginBottom: 20 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail the forms several weeks before the deadline to ensure
                      timely arrival at the IRS.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      You are required to provide a copy of the 1099-Q to the
                      recipient for accurate tax reporting on their part.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Paper filing involves printing, mailing, and tracking
                      forms. As an alternative, you can choose to file 1099-Q
                      forms electronically for faster processing and delivery.
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-Q - Mailing address
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-Q varies depending on your business location. Below is a
                  table summarizing the mailing address for Form 1099-Q:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-Q to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 1099-Q with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before initiating the e-filing process for Form 1099-Q,
                  it&apos;s essential to have all the necessary information
                  ready to ensure a smooth and efficient filing experience.
                  Here&apos;s what you&apos;ll need:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-Q:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Payer/Trustee details.</li>
                  <li style={{ marginBottom: 16 }}>Recipient details.</li>
                  <li style={{ marginBottom: 16 }}>
                    Payment details: Gross Distribution, Earnings and Basis
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Once you have gathered all the required information, you can
                  proceed with the e-filing process using TaxZerone in three
                  simple steps:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1: Complete Form 1099-Q</b>
                  <br />
                  Fill out the form with the necessary details, including payer
                  and recipient information, gross distribution, and
                  distribution code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review the information entered to ensure accuracy,
                  and then transmit the completed Form 1099-Q to the IRS
                  electronically.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After transmitting the form to the IRS, send a copy of the
                  completed Form 1099-Q to the recipient for their tax reporting
                  purposes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of e-filing Form 1099-Q with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  When you choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-q/ "
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-Q
                  </Link>{' '}
                  with TaxZerone, you can enjoy the following advantages:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS form validations:</b> Ensure accuracy by checking
                      your returns for potential errors or missing information.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Email recipient copies:</b> Conveniently send
                      recipient copies via email.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Secure portal access (ZeroneVault):</b> Provide
                      recipients with secure access to their return copies
                      anytime, anywhere, through the ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Competitive pricing:</b> Enjoy industry-leading
                      pricing, with rates as low as $0.59 per form.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By leveraging TaxZerone&apos;s e-filing solution, you can
                  streamline the Form 1099-Q filing process, ensuring accuracy,
                  efficiency, and compliance with IRS requirements.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize your benefits by choosing TaxZerone{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              for e-filing Form 1099-Q
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099qinstructions`}
              >
                E-file Form 1099-Q
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099QInstructions;
